// Minimal CSV utilities — no third-party dep. Handles the common cases:
//   - Comma separators, optional quoted strings
//   - Escaped quotes inside quotes ("")
//   - CRLF or LF line endings
//   - Trailing newline (ignored)
//
// Does NOT handle:
//   - Multiline cell values (newlines inside quoted strings)
//   - Tab/semicolon delimiters (only comma)
// These are rare for client roster imports; we'll extend if needed.

export type CsvParseResult = {
  headers: string[]; // header row, lowercased + trimmed
  rows: Record<string, string>[]; // each row as an object keyed by header
};

export function parseCsv(text: string): CsvParseResult {
  // Normalize line endings, drop trailing whitespace
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) return { headers: [], rows: [] };
  const lines = normalized.split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue; // skip blank rows
    const values = parseLine(line);
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (values[j] ?? '').trim();
    }
    rows.push(row);
  }
  return { headers, rows };
}

function parseLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        // Escaped quote
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else {
      if (ch === ',') {
        out.push(cur);
        cur = '';
      } else if (ch === '"' && cur === '') {
        // Opening quote at start of cell
        inQuotes = true;
      } else {
        cur += ch;
      }
    }
  }
  out.push(cur);
  return out;
}

// Format a row of values back into a single CSV line. Cells with commas, quotes,
// or newlines are quoted; embedded quotes are escaped to "".
export function formatCsvLine(values: (string | number | null | undefined)[]): string {
  return values.map(formatCell).join(',');
}

function formatCell(value: string | number | null | undefined): string {
  if (value == null) return '';
  const s = String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Trigger a CSV download in the browser. Wraps the content in a Blob, creates an
// object URL, and clicks an invisible <a download>. Works in every modern browser.
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to start the download
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ============================================================================
 * Client-CSV column detection — maps known header aliases to our canonical fields.
 * Lowercased + trimmed comparison so "First Name", "FIRSTNAME", "firstname", and
 * "first_name" all resolve to first_name.
 * ============================================================================ */

const ALIASES: Record<
  'first_name' | 'last_name' | 'phone' | 'notes' | 'name' | 'birthday',
  string[]
> = {
  first_name: ['first_name', 'firstname', 'first name', 'fname', 'given name', 'given_name'],
  last_name: ['last_name', 'lastname', 'last name', 'lname', 'surname', 'family name', 'family_name'],
  phone: ['phone', 'phone_number', 'phone number', 'phonenumber', 'mobile', 'cell', 'tel', 'telephone'],
  notes: ['notes', 'note', 'comments', 'comment', 'memo'],
  // Single "name" column = "first last" — split on first space
  name: ['name', 'full name', 'full_name', 'fullname', 'client', 'client name', 'client_name'],
  // Birthday: accept the friendly mm/dd-only header AND the common full-DOB
  // aliases that booking platforms emit. The parser strips the year on
  // import — see parseBirthdayCell() — so dob/birth_date sources land as
  // month+day only. We NEVER store a year, no matter what's in the cell.
  birthday: [
    'birthday_mmdd',
    'birthday',
    'birth_date',
    'birthdate',
    'birth date',
    'dob',
    'date_of_birth',
    'date of birth',
  ],
};

export type ClientColumnMap = {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  notes: string | null;
  name: string | null; // single name column fallback
  birthday: string | null;
};

export function detectClientColumns(headers: string[]): ClientColumnMap {
  const map: ClientColumnMap = {
    first_name: null,
    last_name: null,
    phone: null,
    notes: null,
    name: null,
    birthday: null,
  };
  for (const h of headers) {
    for (const [field, aliases] of Object.entries(ALIASES) as Array<[keyof ClientColumnMap, string[]]>) {
      if (map[field]) continue; // already mapped — first match wins
      if (aliases.includes(h)) {
        map[field] = h;
        break;
      }
    }
  }
  return map;
}

// Extract a client record from a parsed row using the column map.
// Falls back to splitting a single "name" column on the first space if first_name is unset.
// Birthday cell is parsed forgivingly — never fails the row on a bad value;
// just lands as null. See parseBirthdayCell() for accepted formats.
export function rowToClient(
  row: Record<string, string>,
  map: ClientColumnMap
): {
  first_name: string;
  last_name: string;
  phone: string | null;
  notes: string | null;
  birthday_month: number | null;
  birthday_day: number | null;
} | null {
  let firstName = map.first_name ? (row[map.first_name] ?? '').trim() : '';
  let lastName = map.last_name ? (row[map.last_name] ?? '').trim() : '';

  if (!firstName && map.name) {
    const fullName = (row[map.name] ?? '').trim();
    if (fullName) {
      const parts = fullName.split(/\s+/);
      firstName = parts[0] ?? '';
      lastName = parts.slice(1).join(' ');
    }
  }

  if (!firstName && !lastName) return null;

  const phone = map.phone ? normalizePhone(row[map.phone] ?? '') : null;
  const notes = map.notes ? (row[map.notes] ?? '').trim() || null : null;
  const bday = map.birthday ? parseBirthdayCell(row[map.birthday] ?? '') : null;
  return {
    first_name: firstName,
    last_name: lastName,
    phone: phone || null,
    notes,
    birthday_month: bday?.month ?? null,
    birthday_day: bday?.day ?? null,
  };
}

/**
 * Forgiving birthday cell parser for CSV import. Accepts:
 *   - "mm/dd"            (our canonical export format)
 *   - "m/d"              (one-digit shortcuts)
 *   - "mm-dd"            (some platforms emit this)
 *   - "mm/dd/yyyy"       (booking-app full DOB — year stripped, by policy)
 *   - "yyyy-mm-dd"       (ISO DOB — year stripped)
 *   - "yyyy/mm/dd"       (year stripped)
 *
 * Returns null on empty, malformed, or out-of-range values. NEVER stores or
 * returns the year — even if the source had one. This is the same privacy-
 * light rule the in-app field enforces (see migration 0062).
 *
 * Rejects impossible combos (Feb 30, Apr 31) but accepts Feb 29 since
 * storage is year-less.
 */
export function parseBirthdayCell(raw: string): { month: number; day: number } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let month: number | null = null;
  let day: number | null = null;

  // Try yyyy-mm-dd / yyyy/mm/dd first (year is the 4-digit chunk up front).
  const isoMatch = trimmed.match(/^\d{4}[\/-](\d{1,2})[\/-](\d{1,2})$/);
  if (isoMatch) {
    month = parseInt(isoMatch[1] ?? '', 10);
    day = parseInt(isoMatch[2] ?? '', 10);
  } else {
    // mm/dd[/yyyy] or m/d[/yyyy] or mm-dd[-yyyy].
    const mdMatch = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})(?:[\/-]\d{2,4})?$/);
    if (mdMatch) {
      month = parseInt(mdMatch[1] ?? '', 10);
      day = parseInt(mdMatch[2] ?? '', 10);
    }
  }

  if (month == null || day == null) return null;
  if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  // Reject combos that don't exist (Feb 30, Apr 31, etc.). Year 2000 is a
  // leap year, so Feb 29 is accepted — matches our app-level rule.
  const probe = new Date(2000, month - 1, day);
  if (probe.getMonth() !== month - 1 || probe.getDate() !== day) return null;

  return { month, day };
}

/** Inverse of parseBirthdayCell — format a stored mm/dd for the export CSV.
 *  Empty string when either column is null so the cell renders blank. */
export function formatBirthdayCell(
  month: number | null,
  day: number | null,
): string {
  if (month == null || day == null) return '';
  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
}

// Strip everything except digits. We persist the cleaned digits-only string so dedupe
// against existing clients can compare apples-to-apples regardless of formatting.
export function normalizePhone(raw: string): string {
  return raw.replace(/\D/g, '');
}

// Returns the last 10 digits — US-friendly fingerprint for dedupe across formats
// like "+1 (555) 123-4567" and "5551234567" both → "5551234567".
export function phoneFingerprint(raw: string | null | undefined): string {
  const digits = normalizePhone(raw ?? '');
  return digits.slice(-10);
}
